"use client";

import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query. Returns whether it currently matches.
 *
 * On first render (incl. SSR) returns `false`, then synchronously updates
 * during the first effect tick on the client. Components that need to avoid
 * a layout flash can also pass an `initial` value to seed the SSR-render
 * state to whatever they expect on the dominant breakpoint.
 *
 * @example
 *   const isDesktop = useMediaQuery("(min-width: 1024px)");
 *   const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)", true);
 */
export function useMediaQuery(query: string, initial: boolean = false): boolean {
  const [matches, setMatches] = useState<boolean>(initial);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, [query]);

  return matches;
}
